
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import Form from "../forms/form/Form";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import LoadingZone from "../loading-zone/LoadingZone";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";
import AlarmSchemeEditorUtils, { AlarmSchemeAdminMode, AlarmSchemeFormType } from "./AlarmSchemeEditorUtils";
import { IEditDTO, defaultEditAlarmScheme } from "../../services/alarm-scheme-data-service/Models";
import { Box, Button, Stack, Typography } from "@mui/material";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import { ContextActionMenu } from "../contextual-app-bar/ContextActionMenu";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { useAlarmSchemeReferenceData } from "../../queries/alarm-scheme-admin/UseAlarmSchemeReferenceData";
import { useGetAlarmScheme } from "../../queries/alarm-scheme-admin/UseGetAlarmScheme";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { useEffect, useState, type JSX } from "react";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import FormInput from "../forms/form-input/FormInput";
import FormProperty from "../forms/form-property/FormProperty";
import { AlarmSchemeTierCard  } from "../alarm-scheme-tier-card/AlarmSchemeTierCard";
import { AlarmSchemeTierType, TierActions, tierActionItems } from "../alarm-scheme-tier-card/AlarmSchemeTierUtils";
import TierEditorDialog, { bindTierEditorDialog, useTierEditorDialogState } from "./tier-editor/TierEditorDialog";
import { defaultEditTier } from "./tier-editor/TierEditorUtils";
import NoData from "../no-data/NoData";
import { useSaveAlarmScheme } from "../../queries/alarm-scheme-admin/UseSaveAlarmScheme";
import { isMobile, isTablet } from "react-device-detect";

export type AlarmSchemeEditorCloseType = {
    reason: "cancel" | "save";
    alarmScheme: IEditDTO | undefined;
};

export type AlarmSchemeEditorProps = {
    groupID: number;
    organizationID: number;
    alarmSchemeID: number | undefined;
    onClose: ({ reason }: AlarmSchemeEditorCloseType) => void;
};

export default function AlarmSchemeEditor({
    groupID,
    organizationID,
    alarmSchemeID,
    onClose,
}: AlarmSchemeEditorProps): JSX.Element {

    const [nextID, setNextID] = useState(-1);
    
    const isPhone = isMobile && !isTablet;
    
    const [userState] = useUserContext();
    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();

    const tierEditorDialog = useTierEditorDialogState();

    const mode = (alarmSchemeID ?? 0) <= 0 ? 
        AlarmSchemeAdminMode.Add : 
        AlarmSchemeAdminMode.Edit;

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data, status } = useQueryNotifications(useGetAlarmScheme)({
        //organizationID: group?.organizationID as number,        
        organizationID,
        groupID,
        alarmSchemeID: alarmSchemeID as number,
        enabled: group !== undefined
    });

    const form = useForm<AlarmSchemeFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultEditAlarmScheme
        }
    });

    const {
        getValues,
        watch,
        trigger,
        formState: {
            //dirtyFields,
            isDirty,
            isValid,
            isSubmitting
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;

    const hasChanges = isDirty;

    const alarmSchemeForm = useWatch({ 
        control: control 
    }) as AlarmSchemeFormType;


    const {
        //fields: tiers,
        append,
        remove,
        update
    } = useFieldArray({
        control: control,
        name: 'tiers',
        keyName: "fieldKey"
    });    

    const [
        name,
        watchTiers
        ] = watch([
            'name',
            'tiers'
    ]);

    //const organizationID = group?.organizationID as number;

    const {data: referenceData, refetch: refetchReferenceData} = useQueryNotifications(useAlarmSchemeReferenceData)({
        groupID ,
        organizationID: organizationID,
        enabled: groupID !== undefined
    });

    //const tierModels = AlarmSchemeEditorUtils.mapToTierModel(alarmSchemeForm, referenceData);
    const tierModels = AlarmSchemeEditorUtils.mapToTierModel(watchTiers, referenceData);

    const { mutate: saveAlarmScheme } = useQueryNotifications(useSaveAlarmScheme)({
        groupID: groupID,
        organizationID: organizationID,
        onSuccess: async (data) => {
            
            acknowledge(t(mode === AlarmSchemeAdminMode.Add ? 
                            "alarmSchemeAdmin.successMessageAdded" : 
                            "alarmSchemeAdmin.successMessageUpdated"
                        ));
            onClose({
                reason: "save",
                alarmScheme: data
            });
            reset();
        }
    });

    async function onAddTier() {


        const result = await tierEditorDialog.open({
            groupID,
            organizationID,
            item: {...defaultEditTier,
                id: nextID,
                notifications: [],
                schedules: [],
                scheduledNotifications: [],
                useScheduledNotifications: false,
            },
            tiers: watchTiers
        });

        if (result.reason === 'save') {

            setNextID(nextID - 1)

            const formTier = result.values?.tier;
            if (formTier) {

                const newTier = AlarmSchemeEditorUtils.mapFormToTierModel(formTier)
                append(newTier);


                await refetchReferenceData();
            }

        }
    }

    async function onEditTier(tier: AlarmSchemeTierType) {

        const result = await tierEditorDialog.open({
            groupID,
            organizationID,
            item: {...tier
            },
            //tierNames: watchTiers.filter(o => o.name !== tier.name).map(o => o.name)
            tiers: watchTiers
        });

        if (result.reason === 'save') {


            const formTier = result.values?.tier;
            if (formTier) {

                const updatedTier = AlarmSchemeEditorUtils.mapFormToTierModel(formTier)

                const ndx = watchTiers.findIndex(o => o.id === tier.id);

                update(ndx, updatedTier);
            }
        }
    }

    async function onDeleteTier(tier: AlarmSchemeTierType) {


        const result = await confirm({
                dialogTitle: t("alarmSchemeAdmin.tiers.delete.title"),
                confirmationText: t("alarmSchemeAdmin.tiers.delete.text", {
                    name: tier.name
                }),
                yesButtonText: t("buttons.yesContinue"),
                noButtonText: t("buttons.noCancel"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: true,
                showCloseButton: false
            });
            
        if (result === "yes"){

            const ndx = watchTiers.findIndex(o => o.id === tier.id);

            remove(ndx);
        }
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        const action = menuItem.action as TierActions;

        switch (action) {
            case TierActions.Add:
                onAddTier();
                break;
        }
    }

    async function onOkay(formValues: AlarmSchemeFormType) {

        const values = getValues();
        const model = AlarmSchemeEditorUtils.mapToModel(values)

        saveAlarmScheme(model);
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ 
                alarmScheme: alarmSchemeForm,
                reason: "cancel" 
            });
            reset();
        }
    }


    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }


    function onAction(action: TierActions, tier: AlarmSchemeTierType) {
        switch(action) {
            case TierActions.Edit:
                onEditTier(tier);
                break;

            case TierActions.Delete:
                onDeleteTier(tier);
                break;
        }
    }

    const _validateName = (value: string): boolean => {
        return value.toString().trim().length <= 50;
    }


    useEffect(() => {

        if (data) {

            reset(AlarmSchemeEditorUtils.mapToFormValues(data), {keepDirtyValues: true});
        }
    }, [
        data,
        reset
    ]);

  // execute on every render
  useEffect(() => {

    setTimeout(() => {
        let items: ('name')[] = [];

        if ((name?.trim().length ?? 0) === 0) {
            items.push('name');
        }

        if (items.length) {
            trigger(items);
        }

    }, 1);
});

    return (
        <LoadingZone isLoading={status === 'loading'}>
            <SettingsBar 
                title={t(mode === AlarmSchemeAdminMode.Add ? 'buttons.addAlarmScheme' : 'buttons.editAlarmScheme')  } 
                
                onClose={
                    async (reason: SettingsActionEnum) => {
                        reason === SettingsActionEnum.OK
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }}
                modified={isDirty && isValid && watchTiers.length > 0 && !isSubmitting} />
            {data && <Form form={form} hideFormScroll={true}>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                    {isPhone ? 
                    <ContextActionMenu 
                        viewOptionState={ViewOptionStateEnum.Hidden}
                        actions={tierActionItems}
                        onExecuteAction={onExecuteAction}
                        hideOptionsAction={true}
                        buttonColor="default"
                        hideAddToFavoritesAction={true}
                    /> : 
 
                    tierActionItems.map((o, index) => 
                        <Button key={index}
                            onClick={() => onExecuteAction(o)}
                        >
                            {o.label}
                        </Button>
                    )}
 

                </Box>

                <FormPropertySection label={t('properties.standard')}>

                    <FormProperty>
                        <FormInput
                            name="name"
                            label={t('properties.name')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('alarmSchemeAdmin.nameRequired')
                                },
                                validate: (value: string) => !_validateName(value) ?
                                t('alarmSchemeAdmin.nameLength') : 
                                true
                            }}

                        />
                    </FormProperty>
                    
                </FormPropertySection>

                {tierModels.length === 0 ?

                    <NoData 
                        title={t('alarmSchemeAdmin.tiers.noTiers.title')} 
                        text={t('alarmSchemeAdmin.tiers.noTiers.text')} />
                    :

                    <>

                        <Typography variant="subtitle1" sx={{
                            fontWeight: 600
                        }} >
                            {t('properties.tiers')}
                        </Typography>


                        <Stack direction='column' 
                            sx={{
                            overflow: 'hidden',
                            flex: '1 1 auto'
                        }}>


                            <Box sx={{
                                overflow: 'hidden',
                                overflowY: 'auto',
                            }}>


                            {
                                tierModels.map(o => 
                                    <AlarmSchemeTierCard key={o.id} 
                                        item={o} 
                                        dense={false} 
                                        sx={{
                                            marginLeft: 1
                                        }}
                                        allowEdit={true}
                                        onAction={onAction}
                                    />
                                )
                            }



                            </Box>
                        </Stack>        
                    </>
                }            

            </Form>
            }
            <TierEditorDialog {...bindTierEditorDialog(tierEditorDialog)} />
            <UnsavedChangesPrompt
                hasChanges={hasChanges}
                getUserConfirmation={getUserConfirmation}
            />
        </LoadingZone>
    );
}
